import { collectShapeIdsDeep } from './UmlCanvasEngineHelper';

/**
 * Computes a presentation-only derived view of shapes/connectors with
 * user-hidden items (shapes and/or connectors, viewConfig.userHiddenItems)
 * skipped from paint — never mutates the real shapes/connectors. Same
 * pattern as resolveInterfaceReduction.js, and meant to be chained BEFORE
 * it: hiding is the more fundamental manual toggle, interface-reduction
 * then operates on whatever remains visible.
 * @param {object[]} shapes - umlCanvasEngine.shapes (top-level)
 * @param {object[]} connectors - umlConnectorEngine.connectors
 * @param {object} viewConfig - umlCanvasEngine.viewConfig
 * @returns {{visibleShapes: object[], visibleConnectors: object[]}}
 */
export function resolveUserHiddenItems(shapes, connectors, viewConfig) {
    const hiddenIds = new Set(viewConfig.userHiddenItems || []);
    if (hiddenIds.size === 0) {
        return { visibleShapes: shapes, visibleConnectors: connectors };
    }

    const visibleShapes = filterHiddenShapes(shapes, hiddenIds);
    const visibleIds = new Set(visibleShapes.flatMap(collectShapeIdsDeep));
    const visibleConnectors = connectors.filter(function filterOutHiddenConnector(connector) {
        return (
            !hiddenIds.has(connector.id) &&
            visibleIds.has(connector.fromShapeId) &&
            visibleIds.has(connector.toShapeId)
        );
    });

    return { visibleShapes, visibleConnectors };
}

/**
 * Recursively drops shapes whose id is in hiddenIds — cascades to children:
 * a hidden package takes everything nested inside it with it, regardless of
 * a child's own hidden-state.
 * @param {object[]} shapes
 * @param {Set<string>} hiddenIds
 * @returns {object[]}
 */
function filterHiddenShapes(shapes, hiddenIds) {
    return shapes
        .filter(function filterOutHiddenShape(shape) {
            return !hiddenIds.has(shape.id);
        })
        .map(function reduceChildren(shape) {
            if (!shape.children) return shape;
            const filteredChildren = filterHiddenShapes(shape.children, hiddenIds);
            if (filteredChildren === shape.children) return shape;
            // Object.create + assign, not {...shape} — plain spread loses the
            // prototype chain, breaking `instanceof` checks downstream
            // (UmlCanvasHelpers.renderShape), same lesson as
            // resolveInterfaceReduction.filterHiddenShapes.
            const clone = Object.create(Object.getPrototypeOf(shape));
            Object.assign(clone, shape, { children: filteredChildren });
            return clone;
        });
}
